import React, { useState, useEffect } from 'react';
import Header from '../../Header/Header.jsx';
import Footer from '../../Footer/Footer.jsx';
import aiCollaborateVideo from '../../../images/ai.mp4';
import styles from './AICollaborate.module.css';

const AICollaborateProjectShowcase = () => {
  const [hoveredTool, setHoveredTool] = useState(null);
  const [hoveredResponsibility, setHoveredResponsibility] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [mode, setMode] = useState('web');
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const responsibilities = [
    { text: 'Idea Generation and Conceptualization', icon: '💡' },
    { text: 'User Experience (UX) and Interface (UI) Design', icon: '🎨' },
    { text: 'Mobile Responsiveness', icon: '📱' },
    { text: 'WordPress Development', icon: '⚙️' },
    { text: 'E-Commerce Functionality', icon: '🛒' },
    { text: 'Performance Optimization', icon: '⚡' },
    { text: 'Security Measures', icon: '🔒' }
  ];

  const tools = [
    { name: 'Tailwind', icon: '🎨', description: 'CSS Framework' },
    { name: 'Svelte', icon: '⚡', description: 'Web Framework' },
    { name: 'WordPress', icon: '🌐', description: 'CMS Platform' },
    { name: 'Figma', icon: '🎯', description: 'Design Tool' },
    { name: 'Photoshop', icon: '🖼️', description: 'Image Editor' },
    { name: 'Illustrator', icon: '✨', description: 'Vector Graphics' }
  ];

  const skills = [
    'HTML5', 'CSS3', 'JavaScript', 'Svelte', 'Tailwind CSS',
    'WordPress', 'Figma', 'REST API', 'AI Integration', 'Responsive Design',
    'UI/UX Design', 'Photoshop', 'Illustrator', 'SEO', 'Performance Optimization'
  ];

  return (
    <>
      <Header mode={mode} setMode={setMode} menuOpen={menuOpen} setMenuOpen={setMenuOpen} scrolled={scrolled} />
      <div className={styles.container}>
      {/* Animated Background */}
      <div className={styles.backgroundGradient}>
        <div className={styles.gradientOrb1}></div>
        <div className={styles.gradientOrb2}></div>
        <div className={styles.gradientOrb3}></div>
        <div className={styles.gridOverlay}></div>
      </div>

      <div className={styles.contentWrapper}>
        {/* Hero Section */}
        <section className={styles.heroSection}>
          <div className={styles.heroContent}>
            <div className={styles.heroLeft}>
              <div className={styles.projectBadge}>
                <span className={styles.badgeDot}></span>
                <span>Ongoing Project</span>
              </div>
              
              <h1 className={styles.projectTitle}>AI Solutions Platform</h1>
              <p className={styles.projectSubtitle}>Elevate Business through AI Collaboration & Innovation</p>
              
              <div className={styles.projectMeta}>
                <div className={styles.metaItem}>
                  <span className={styles.metaLabel}>Work</span>
                  <span className={styles.metaValue}>Website Design</span>
                </div>
                <div className={styles.metaItem}>
                  <span className={styles.metaLabel}>Year</span>
                  <span className={styles.metaValue}>2023</span>
                </div>
                <div className={styles.metaItem}>
                  <span className={styles.metaLabel}>Duration</span>
                  <span className={styles.metaValue}>Oct 2023 - Ongoing</span>
                </div>
              </div>
              
              <p className={styles.heroDescription}>
                The platform fosters a vibrant AI community, connecting professionals, researchers, and organizations to jointly tackle projects, share AI solutions, and drive collaborative research and development.
              </p>
              
              
            </div>
            
            <div className={styles.heroRight}>
              <div className={styles.heroVisual}>
                <video 
                  className={styles.heroVideo}
                  autoPlay 
                  loop 
                  muted 
                  playsInline
                >
                  <source src={aiCollaborateVideo} type="video/mp4" />
                </video>
              </div>
            </div>
          </div>
        </section>


        {/* Introduction Section */}
        <section className={styles.section}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>Introduction</h2>
            <div className={styles.titleUnderline}></div>
          </div>
          <div className={styles.contentCard}>
            <p className={styles.contentText}>
              Specializing in cutting-edge AI solutions, we excel in crafting custom AI applications tailored to your unique needs. Our expertise extends to seamless AI integration with existing systems, comprehensive AI risk assessments, strategic AI planning, meticulous data analysis, and preparation. Committed to knowledge-sharing, we offer education and training programs. Stay ahead with our AI market research and trends analysis, ensuring you're at the forefront of innovation. Elevate your business with our end-to-end AI services, empowering you to harness the full potential of artificial intelligence.
            </p>
          </div>
        </section>

        {/* My Role Section */}
        <section className={styles.section}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>My Role</h2>
            <div className={styles.titleUnderline}></div>
          </div>
          <div className={styles.contentCard}>
            <p className={styles.contentText}>
              As a Website Designer at our dynamic AI-centric firm, I served as the creative force behind the online presence. Leveraging expertise in Tailwind CSS, I crafted visually stunning and intuitive interfaces focused on delivering curated content to users based on their search queries. Contributing to the innovative team, I helped reshape the digital landscape by creating seamless user experiences that connect AI professionals and drive collaborative innovation.
            </p>
          </div>
        </section>

        {/* Responsibilities Section */}
        <section className={styles.section}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>Responsibilities</h2>
            <div className={styles.titleUnderline}></div>
          </div>
          <div className={styles.responsibilitiesGrid}>
            {responsibilities.map((responsibility, index) => (
              <div 
                key={index}
                className={styles.responsibilityCard}
                style={{ animationDelay: `${index * 0.1}s` }}
                onMouseEnter={() => setHoveredResponsibility(index)}
                onMouseLeave={() => setHoveredResponsibility(null)}
              >
                <span className={styles.cardNumber}>{String(index + 1).padStart(2, '0')}</span>
                <div className={styles.cardIcon}>{responsibility.icon}</div>
                <div className={styles.cardText}>{responsibility.text}</div>
              </div>
            ))}
          </div>
        </section>

        {/* Tools Section */}
        <section className={styles.section}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>Tools Used</h2>
            <div className={styles.titleUnderline}></div>
          </div>
          <div className={styles.toolsGrid}>
            {tools.map((tool, index) => (
              <div 
                key={index}
                className={styles.toolCard}
                style={{ animationDelay: `${index * 0.15}s` }}
                onMouseEnter={() => setHoveredTool(index)}
                onMouseLeave={() => setHoveredTool(null)}
              >
                <div className={styles.toolGlow}></div>
                <div className={styles.toolIcon}>{tool.icon}</div>
                <div className={styles.toolName}>{tool.name}</div>
                <div className={styles.toolDescription}>{tool.description}</div>
              </div>
            ))}
          </div>
        </section>

        {/* Technologies & Skills Section */}
        <section className={styles.section}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>Technologies & Skills</h2>
            <div className={styles.titleUnderline}></div>
          </div>
          <div className={styles.skillsContainer}>
            {skills.map((skill, index) => (
              <div
                key={index}
                className={styles.skillTag}
                style={{ animationDelay: `${index * 0.05}s` }}
              >
                <span className={styles.skillHash}>#</span>
                {skill}
              </div>
            ))}
          </div>
        </section>

        
      </div>
      </div>
      <div className={styles.footerOverride}>
        <Footer />
      </div>
    </>
  );
};

export default AICollaborateProjectShowcase;